import React, { useCallback, useEffect, useState } from 'react';
import { FaCheckCircle, FaRedo, FaTimes, FaTimesCircle } from 'react-icons/fa';
import { drawQuizQuestions, gradeQuiz, recordQuizAttempt } from '../../utils/questionPool';
import styles from './PoolQuiz.module.css';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * Okuma sonu sınavı: çalışanın departman havuzundan rastgele sorular sorar.
 * Geçilirse `onPassed` çağrılır; kalınırsa yeni sorularla tekrar denenir.
 * Havuzda soru yoksa sınav atlanır ve `onPassed({ skipped: true })` çağrılır.
 */
const PoolQuiz = ({ show, group, source, user, onPassed, onClose }) => {
  const [phase, setPhase] = useState('loading'); // loading | error | quiz | result
  const [questions, setQuestions] = useState([]);
  const [passScore, setPassScore] = useState(70);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const startAttempt = useCallback(async () => {
    setPhase('loading');
    setIndex(0);
    setAnswers({});
    setResult(null);
    try {
      const drawn = await drawQuizQuestions(group);
      if (drawn.questions.length === 0) {
        onPassed({ skipped: true });
        return;
      }
      setQuestions(drawn.questions);
      setPassScore(drawn.passScore);
      setPhase('quiz');
    } catch (error) {
      console.error('Sınav soruları yüklenemedi:', error);
      setPhase('error');
    }
  }, [group, onPassed]);

  useEffect(() => {
    if (show) startAttempt();
    // Yalnızca pencere açıldığında yeni deneme başlar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const current = questions[index];
  const answeredAll = questions.every((question) => answers[question.id] !== undefined);

  const finish = async () => {
    const graded = gradeQuiz(questions, answers, passScore);
    setResult(graded);
    setPhase('result');
    await recordQuizAttempt({
      userId: user?.uid || '',
      employeeId: user?.employeeId || '',
      employeeName: user?.name || '',
      group: group || '',
      sourceType: source.type,
      sourceId: source.id,
      sourceName: source.name || '',
      questionIds: questions.map((question) => question.id),
      questionCount: questions.length,
      correctCount: graded.correctCount,
      score: graded.score,
      passScore,
      passed: graded.passed,
    });
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="pool-quiz-title">
      <div className={styles.panel}>
        <header className={styles.top}>
          <div>
            <h2 id="pool-quiz-title">Okuma Sonu Değerlendirme</h2>
            <p>{source.name}</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </header>

        {phase === 'loading' && <div className={styles.center}>Sorular hazırlanıyor...</div>}

        {phase === 'error' && (
          <div className={styles.center} role="alert">
            <p>Sorular yüklenemedi. Lütfen tekrar deneyin.</p>
            <button type="button" className={styles.mainBtn} onClick={startAttempt}>
              <FaRedo /> Tekrar Dene
            </button>
          </div>
        )}

        {phase === 'quiz' && current && (
          <>
            <div className={styles.progress}>
              <span>Soru {index + 1} / {questions.length}</span>
              <span>Geçme notu: %{passScore}</span>
            </div>
            <div className={styles.track}>
              <div className={styles.trackFill} style={{ width: `${((index + 1) / questions.length) * 100}%` }} />
            </div>

            <div className={styles.body}>
              <h3 className={styles.questionText}>{current.question}</h3>
              <div className={styles.choices} role="radiogroup" aria-label="Şıklar">
                {current.options.map((option, optionIndex) => {
                  const selected = answers[current.id] === optionIndex;
                  return (
                    <button
                      // Şıkların kalıcı kimliği yok; sıra deneme boyunca sabittir
                      key={optionIndex}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`${styles.choice} ${selected ? styles.choiceSelected : ''}`}
                      onClick={() => setAnswers((prev) => ({ ...prev, [current.id]: optionIndex }))}
                    >
                      <span className={styles.letter}>{LETTERS[optionIndex]}</span>
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <footer className={styles.bottom}>
              <button type="button" className={styles.plainBtn} onClick={() => setIndex(index - 1)} disabled={index === 0}>
                Önceki
              </button>
              {index < questions.length - 1 ? (
                <button
                  type="button"
                  className={styles.mainBtn}
                  onClick={() => setIndex(index + 1)}
                  disabled={answers[current.id] === undefined}
                >
                  Sonraki
                </button>
              ) : (
                <button type="button" className={styles.mainBtn} onClick={finish} disabled={!answeredAll}>
                  Sınavı Bitir
                </button>
              )}
            </footer>
          </>
        )}

        {phase === 'result' && result && (
          <>
            <div className={styles.body}>
              <div className={`${styles.score} ${result.passed ? styles.scorePass : styles.scoreFail}`}>
                {result.passed ? <FaCheckCircle /> : <FaTimesCircle />}
                <strong>%{result.score}</strong>
                <span>
                  {result.correctCount} / {questions.length} doğru ·{' '}
                  {result.passed ? 'Başarılı' : `Geçme notu %${passScore}`}
                </span>
              </div>

              <ul className={styles.review}>
                {questions.map((question, questionIndex) => {
                  const correct = answers[question.id] === question.correctAnswer;
                  return (
                    <li key={question.id} className={correct ? styles.reviewRight : styles.reviewWrong}>
                      <p>
                        {questionIndex + 1}. {question.question}
                      </p>
                      {/* Kalan kullanıcıya doğru cevap gösterilmez: aynı soru tekrar çıkabilir */}
                      {(correct || result.passed) && (
                        <small>Doğru cevap: {question.options[question.correctAnswer]}</small>
                      )}
                      {result.passed && question.explanation && <small>{question.explanation}</small>}
                    </li>
                  );
                })}
              </ul>
            </div>

            <footer className={styles.bottom}>
              {result.passed ? (
                <button type="button" className={styles.mainBtn} onClick={() => onPassed({ score: result.score })}>
                  Tamamla
                </button>
              ) : (
                <>
                  <button type="button" className={styles.plainBtn} onClick={onClose}>
                    Dokümana Dön
                  </button>
                  <button type="button" className={styles.mainBtn} onClick={startAttempt}>
                    <FaRedo /> Yeni Sorularla Tekrar Dene
                  </button>
                </>
              )}
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default PoolQuiz;
